import { OPENER_KIND_INFO, OPENER_STYLE_INHERIT } from "@loot-os/api-contract";
import { Label } from "@loot-os/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";

import type { OpenerStyleChoice } from "../lib/openerStyles";

/**
 * Picks which opener style something opens with, or none to inherit the next level up (named with
 * what that currently resolves to). Saves on change; the caller toasts.
 */
export function OpenerStyleSelect({
  id,
  label,
  description,
  styles,
  value,
  inheritLabel,
  inheritedStyleId,
  disabled,
  onChange,
}: {
  id: string;
  /** Omit when the caller labels `id` itself (a settings row). */
  label?: string;
  description?: string;
  styles: readonly OpenerStyleChoice[];
  value: string | null;
  /** The inherit option's label, e.g. "Platform default"; omit to require a style. */
  inheritLabel?: string;
  inheritedStyleId?: string | null;
  disabled: boolean;
  onChange: (styleId: string | null) => void;
}) {
  const nameOf = (styleId: string | null | undefined) =>
    styles.find((style) => style.id === styleId)?.name;
  const inherited = nameOf(inheritedStyleId);
  const items: Record<string, string> = {
    ...(inheritLabel
      ? { [OPENER_STYLE_INHERIT]: inherited ? `${inheritLabel} (${inherited})` : inheritLabel }
      : {}),
    ...Object.fromEntries(styles.map((style) => [style.id, style.name])),
  };
  return (
    <div className="grid content-start gap-2">
      {label && <Label htmlFor={id}>{label}</Label>}
      <Select
        value={value ?? OPENER_STYLE_INHERIT}
        items={items}
        disabled={disabled}
        onValueChange={(next) =>
          onChange(next === OPENER_STYLE_INHERIT || next === null ? null : String(next))
        }
      >
        <SelectTrigger id={id} className="w-full sm:w-80">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {inheritLabel && (
            <SelectItem value={OPENER_STYLE_INHERIT}>{items[OPENER_STYLE_INHERIT]}</SelectItem>
          )}
          {styles.map((style) => (
            <SelectItem key={style.id} value={style.id}>
              {style.name}
              <span className="text-muted-foreground">
                {" "}
                · {OPENER_KIND_INFO[style.kind].label}
                {style.scope === "platform" ? " · platform" : ""}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
    </div>
  );
}
